import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["listsPanel", "deviceModelId", "deviceTypeId", "powerPercentageDisplay", "powerPercentageSlider", "accessPointSections", "specsSection", "specsContent"]

  connect() {
    // Expose controller for debugging (dev console)
    this.element.deviceController = this
    
    // Always hide all sections initially
    this._hideAllSections()
    
    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      let deviceTypeId = null
      
      // Try to get device type ID from SimplifiedDropdownComponent hidden input
      const valueInput = document.querySelector('#device_type_id [data-simplified-dropdown-target="valueInput"]')
      if (valueInput && valueInput.value) {
        deviceTypeId = valueInput.value
      }
      
      // If no ID found, try legacy approach
      if (!deviceTypeId && this.hasDeviceTypeIdTarget && this.deviceTypeIdTarget.value) {
        deviceTypeId = this.deviceTypeIdTarget.value
      }
      
      // Update conditional sections by ID
      if (deviceTypeId) {
        this._updateConditionalSectionsByTypeId(deviceTypeId)
      }
    }, 100) // Increased timeout to ensure SimplifiedDropdownComponent is fully initialized
    
    // Initialize power slider
    this._initializePowerSlider()

    const modelValueInput = document.querySelector('#device_model_id [data-simplified-dropdown-target="valueInput"]')
    if (modelValueInput) {
      modelValueInput.addEventListener('change', () => {
        const slider = document.getElementById('power_percentage_slider')
        this._updatePowerPreview(slider ? slider.value : 100)
      })
    }
  }

  onDeviceTypeChange(event) {
    this.resetDeviceModelSelection()
    // Note: Device models are now handled by DeviceFormController.loadDeviceModelsSync()
    // this._renderDeviceModel(event.target.id) // Removed to avoid conflict with SimplifiedDropdownComponent
    
    const deviceTypeId = this._currentDeviceTypeId()
    this._updateConditionalSectionsByTypeId(deviceTypeId)
  }

  // Power slider event handlers
  onPowerSliderChange(event) {
    const percentage = event.target.value
    this._updatePowerDisplay(percentage)
    this._updatePowerPreview(percentage)
  }

  _initializePowerSlider() {
    const slider = document.getElementById('power_percentage_slider')
    if (slider) {
      this._updatePowerDisplay(slider.value)
      this._updatePowerPreview(slider.value)
    }
  }

  _updatePowerDisplay(percentage) {
    if (this.hasPowerPercentageDisplayTarget) {
      this.powerPercentageDisplayTarget.textContent = `${percentage}%`
    }
  }

  _updatePowerPreview(percentage) {
    const refs = this._currentPowerReferences()

    this._updateBandReferenceValue("2.4", refs["2.4"], percentage)
    this._updateBandReferenceValue("5", refs["5"], percentage)
    this._updateBandReferenceValue("6", refs["6"], percentage)
    this._updateReferenceVisibility(refs)
  }

  _currentPowerReferences() {
    const defaults = { "2.4": 20, "5": 22, "6": 22 }
    const typeId = this._currentDeviceTypeId()
    if (!this._isAccessPointType(typeId)) return defaults

    const modelId = this._currentDeviceModelId()
    const models = this._getDeviceModelsForType(typeId)
    const model = models.find(m => String(m.id || m.value) === String(modelId))
    const ap = model?.ap_spec || model?.apSpec
    if (!ap) return defaults

    const normalize = (v, fallback) => {
      const n = Number(v)
      return Number.isFinite(n) && n > 0 ? n : fallback
    }

    return {
      "2.4": normalize(ap.eu_24ghz_dbm, defaults["2.4"]),
      "5": normalize(ap.eu_5ghz_dbm, defaults["5"]),
      "6": normalize(ap.eu_6ghz_dbm, defaults["6"])
    }
  }

  _updateBandReferenceValue(bandKey, referenceDbm, percentage) {
    const row = document.querySelector(`.reference-value[data-band="${bandKey}"]`)
    if (!row) return

    const ratio = Number(percentage) / 100
    const value = this._clipPower((Number(referenceDbm) || 0) * ratio)
    row.textContent = `${bandKey}GHz: ${value.toFixed(1)} dBm`
  }

  _updateReferenceVisibility(refs = this._currentPowerReferences()) {
    const bandKeys = [ "2.4", "5", "6" ]
    bandKeys.forEach((bandKey) => {
      const row = document.querySelector(`.reference-value[data-band="${bandKey}"]`)
      if (!row) return
      row.style.display = this._hasBandData(bandKey, refs) ? "" : "none"
    })
  }

  _hasBandData(bandKey, refs) {
    const typeId = this._currentDeviceTypeId()
    if (!this._isAccessPointType(typeId)) return false

    const modelId = this._currentDeviceModelId()
    const models = this._getDeviceModelsForType(typeId)
    const model = models.find(m => String(m.id || m.value) === String(modelId))
    if (!model) return false

    const rawBands = model.supported_bands || model.supported_frequency_bands || []
    if (!Array.isArray(rawBands) || rawBands.length === 0) return false

    const normalized = rawBands.map((v) => Number(v)).filter((v) => Number.isFinite(v))
    const supported = normalized.some((v) => Math.abs(v - Number(bandKey)) < 0.01)
    if (!supported) return false

    const ref = Number(refs?.[bandKey])
    return Number.isFinite(ref) && ref > 0
  }

  _clipPower(value) {
    return Math.max(0, Math.min(30, value))
  }

  // _renderDeviceModel method removed - now handled by DeviceFormController.loadDeviceModelsSync()
  // This avoids conflicts with SimplifiedDropdownComponent

  selectDeviceModel(event) {
    if(this.hasDeviceModelIdTarget) {
      const id = event.target.id
      this.deviceModelIdTarget.value = id
    }
  }

  resetDeviceModelSelection() {
    // Note: Device model selection is now handled by SimplifiedDropdownComponent
    // The DeviceFormController manages the device model dropdown updates
  }

  _updateConditionalSectionsByName(deviceTypeName) {
    const deviceTypeId = this._currentDeviceTypeId()
    if (deviceTypeId) {
      this._updateConditionalSectionsByTypeId(deviceTypeId)
      return
    }

    if (!deviceTypeName) {
      this._hideAllSections()
      return
    }

    const dtype = (deviceTypeName || "").toLowerCase()

    if (dtype.includes("access point")) {
      this._showAccessPointSections()
    } else if (dtype.includes("switch")) {
      this._showHardcodedSpecs(deviceTypeId)
    } else {
      this._hideAllSections()
    }
  }

  _updateConditionalSectionsByTypeId(deviceTypeId) {
    if (!deviceTypeId) {
      this._hideAllSections()
      return
    }

    if (this._isAccessPointType(deviceTypeId)) {
      this._showAccessPointSections()
    } else if (this._isSwitchType(deviceTypeId)) {
      this._showHardcodedSpecs(deviceTypeId)
    } else {
      this._hideAllSections()
    }
  }

  _getDeviceTypeNameById(deviceTypeId) {
    if (!deviceTypeId) return null
    
    // First try SimplifiedDropdownComponent structure
    const simplifiedDropdown = document.querySelector('#device_type_id [data-simplified-dropdown-target="displayText"]')
    if (simplifiedDropdown && simplifiedDropdown.textContent.trim() !== 'Select value' && simplifiedDropdown.textContent.trim() !== 'Select device type') {
      return simplifiedDropdown.textContent.trim()
    }
    
    // Try to find the correct option from SimplifiedDropdownComponent options
    const dropdown = document.querySelector('#device_type_id')
    if (dropdown) {
      // Check SimplifiedDropdownComponent options
      const option = dropdown.querySelector(`[data-value="${deviceTypeId}"]`)
      if (option) {
        return option.textContent.trim()
      }
      
      // Fallback to legacy structure
      const legacyOption = dropdown.querySelector(`label[id="${deviceTypeId}"]`)
      if (legacyOption) {
        return legacyOption.textContent.trim()
      }
    }
    
    // Alternative: try to get from the legacy dropdown display value
    const legacySelectedText = document.querySelector('#device_type_id_dropdown_input_value')
    if (legacySelectedText && legacySelectedText.textContent.trim() !== 'Select value') {
      return legacySelectedText.textContent.trim()
    }
    
    // Final fallback: use static mapping if all else fails
    const typeMap = {
      '1': 'Access Point',
      '2': 'Switch',
      '3': 'Gateway'
    }
    
    if (typeMap[deviceTypeId]) {
      return typeMap[deviceTypeId]
    }
    
    return null
  }

  _showAccessPointSections() {
    if (this.hasAccessPointSectionsTarget) {
      this.accessPointSectionsTarget.style.display = 'block'
    }
    if (this.hasSpecsSectionTarget) {
      this.specsSectionTarget.style.display = 'none'
    }
  }

  _showHardcodedSpecs(deviceTypeId) {
    if (this.hasSpecsSectionTarget) {
      this.specsSectionTarget.style.display = 'block'
      this._populateSpecs(deviceTypeId)
    }
    if (this.hasAccessPointSectionsTarget) {
      this.accessPointSectionsTarget.style.display = 'none'
      
      // Clear Access Point specific form values when switching to Switch/Gateway
      this._clearAccessPointFormValues()
    }
  }

  _hideAllSections() {
    if (this.hasAccessPointSectionsTarget) {
      this.accessPointSectionsTarget.style.display = 'none'
    }
    if (this.hasSpecsSectionTarget) {
      this.specsSectionTarget.style.display = 'none'
    }
  }

  _populateSpecs(deviceTypeId) {
    if (!this.hasSpecsContentTarget || !this.hasSpecsSectionTarget) return

    if (!this._isSwitchType(deviceTypeId)) {
      this._resetSpecsFields()
      this.specsSectionTarget.style.display = 'none'
      return
    }

    this._ensureSpecsTemplate()

    const currentTypeId = this._currentDeviceTypeId()
    const models = this._getDeviceModelsForType(currentTypeId)
    const currentModelId = this._currentDeviceModelId()
    const model = models.find(m => String(m.id || m.value) === String(currentModelId))
    console.debug("[DeviceController] specs populate", { deviceTypeId: currentTypeId, currentModelId, model, modelsCount: models.length })

    if (!currentModelId || !model) {
      this._resetSpecsFields()
      this.specsSectionTarget.style.display = 'none'
      return
    }

    const sw = model && (model.gateway_spec || model.gatewaySpec || model.switch_spec || model.switchSpec)
    console.debug("[DeviceController] specs data", { sw })
    if (!sw) {
      this._resetSpecsFields()
      this.specsContentTarget.insertAdjacentHTML("beforeend", `<div class="muted">No switch specs available</div>`)
      this.specsSectionTarget.style.display = 'block'
      return
    }

    const fmt = (v) => (v === null || v === undefined || Number.isNaN(Number(v))) ? "-" : v
    const fields = {
      ports_10g: sw.ports_10g,
      ports_5g: sw.ports_5g,
      ports_2_5g: sw.ports_2_5g,
      ports_1g: sw.ports_1g,
      poe_ports_bt: sw.poe_ports_bt,
      poe_ports_at: sw.poe_ports_at,
      poe_ports_af: sw.poe_ports_af,
      poe_budget_watts: sw.poe_budget_watts
    }

    Object.entries(fields).forEach(([key, value]) => {
      const cell = this.specsContentTarget.querySelector(`[data-specs-field="${key}"]`)
      if (cell) cell.textContent = fmt(value)
    })

    this.specsSectionTarget.style.display = 'block'
  }

  _ensureSpecsTemplate() {
    if (this.specsContentTarget.querySelector('[data-specs-field]')) return
    this.specsContentTarget.innerHTML = `
      <table class="specs-table">
        <tr><td>Port</td><td>Amount</td></tr>
        <tr><td>10 G</td><td data-specs-field="ports_10g">-</td></tr>
        <tr><td>5 G</td><td data-specs-field="ports_5g">-</td></tr>
        <tr><td>2.5 G</td><td data-specs-field="ports_2_5g">-</td></tr>
        <tr><td>1 G</td><td data-specs-field="ports_1g">-</td></tr>
        <tr><td>PoE 802.3bt</td><td data-specs-field="poe_ports_bt">-</td></tr>
        <tr><td>PoE 802.3at</td><td data-specs-field="poe_ports_at">-</td></tr>
        <tr><td>PoE 802.3af</td><td data-specs-field="poe_ports_af">-</td></tr>
        <tr><td>PoE Budget (W)</td><td data-specs-field="poe_budget_watts">-</td></tr>
      </table>
    `
  }

  _resetSpecsFields() {
    this.specsContentTarget.querySelectorAll('[data-specs-field]').forEach(cell => { cell.textContent = "-" })
    this.specsContentTarget.querySelectorAll('.muted').forEach(el => el.remove())
  }

  _currentDeviceTypeId() {
    const valueInput = document.querySelector('#device_type_id [data-simplified-dropdown-target="valueInput"]')
    if (valueInput && valueInput.value) return valueInput.value
    if (this.hasDeviceTypeIdTarget && this.deviceTypeIdTarget.value) return this.deviceTypeIdTarget.value
    return null
  }

  _currentDeviceModelId() {
    const valueInput = document.querySelector('#device_model_id [data-simplified-dropdown-target="valueInput"]')
    if (valueInput && valueInput.value) return valueInput.value
    if (this.hasDeviceModelIdTarget && this.deviceModelIdTarget.value) return this.deviceModelIdTarget.value
    return null
  }

  _getDeviceModelsMap() {
    if (this._deviceModelsMap) return this._deviceModelsMap
    const sourceEl = document.querySelector('[data-controller*="device-form"]')
    const raw = sourceEl?.dataset?.deviceFormDeviceModelsMapValue || this.element?.dataset?.deviceFormDeviceModelsMapValue
    try {
      this._deviceModelsMap = raw ? JSON.parse(raw) : {}
    } catch {
      this._deviceModelsMap = {}
    }
    return this._deviceModelsMap
  }

  _normalizeTypeIds(raw) {
    if (!Array.isArray(raw)) return []
    return raw.map(id => String(id))
  }

  _getAccessPointTypeIds() {
    if (this._accessPointTypeIds) return this._accessPointTypeIds
    const sourceEl = document.querySelector('[data-controller*="device-form"]')
    const raw = sourceEl?.dataset?.deviceFormAccessPointTypeIdsValue
    try {
      this._accessPointTypeIds = this._normalizeTypeIds(raw ? JSON.parse(raw) : [])
    } catch {
      this._accessPointTypeIds = []
    }
    return this._accessPointTypeIds
  }

  _getSwitchSpecTypeIds() {
    if (this._switchSpecTypeIds) return this._switchSpecTypeIds
    const sourceEl = document.querySelector('[data-controller*="device-form"]')
    const raw = sourceEl?.dataset?.deviceFormSwitchSpecTypeIdsValue
    try {
      this._switchSpecTypeIds = this._normalizeTypeIds(raw ? JSON.parse(raw) : [])
    } catch {
      this._switchSpecTypeIds = []
    }
    return this._switchSpecTypeIds
  }

  _isAccessPointType(deviceTypeId) {
    if (!deviceTypeId) return false
    return this._getAccessPointTypeIds().includes(String(deviceTypeId))
  }

  _isSwitchSpecType(deviceTypeId) {
    if (!deviceTypeId) return false
    return this._getSwitchSpecTypeIds().includes(String(deviceTypeId))
  }

  _isSwitchType(deviceTypeId) {
    if (!this._isSwitchSpecType(deviceTypeId)) return false
    const typeName = (this._getDeviceTypeNameById(deviceTypeId) || "").toLowerCase()
    return typeName.includes("switch") && !typeName.includes("gateway")
  }

  _getDeviceModelsForType(deviceTypeId) {
    if (!deviceTypeId) return []
    const map = this._getDeviceModelsMap()
    return map?.[deviceTypeId.toString()] || []
  }

  _clearAccessPointFormValues() {
    // Note: Form clearing is now handled by DeviceFormController
    // SimplifiedDropdownComponent automatically manages its own state
    
    // Reset power slider to default (this is still needed)
    const powerSlider = document.getElementById('power_percentage_slider')
    if (powerSlider) {
      powerSlider.value = 75
      this._updatePowerDisplay(75)
      this._updatePowerPreview(75)
    }
  }
}
